<script setup lang="ts">
import { useI18n } from '@/i18n';
import {
  couponInCurrency,
  couponLabel,
  discountDisplayName,
  isUnexpandedDiscountPlaceholder,
  pickDiscountFromSources,
  resolveCouponFromDiscountRow,
} from '@/lib/stripeDiscounts';
import { useBillingStore, type ValidatedCoupon } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import { useBillingUtils } from '@/use/billingUtils';
import { useToast } from '@/use/toast';
import { Button, Icon, LayoutGap, Modal, Prose } from '@dabble/quillui';
import posthog from 'posthog-js';
import type { Stripe } from 'stripe';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * What a promo link (`/billing?coupon=CODE`) does once the user is signed in.
 * Mounted once in App.vue; when the user lands on one of the billing pages a link
 * may point at with a code stashed in sessionStorage, it opens over that page,
 * consumes the code, and applies it the way that fits the account:
 *
 * - a Lifetime gift card → held for checkout, which they go straight to, on the
 *   Lifetime plan the card pays for;
 * - no subscription → the code is stashed for checkout; from the portal they go
 *   on to pick a plan (plans and checkout already are that step);
 * - a subscription with no coupon → applied outright;
 * - a subscription that already has a coupon → both are shown side by side and
 *   they choose which to keep ("Apply new" replaces the Stripe discount);
 * - a lifetime plan, or a bad code → a message, nothing changed.
 *
 * Every outcome but the first two leaves them on the page they landed on.
 *
 * It used to be its own `/billing/coupon` route, which left the page blank on the
 * way out: its root was the Modal's Teleport, which App.vue's out-in route
 * transition can't animate, so the leave finished mid-patch and threw before the
 * next page rendered (DAB-1880, DABBLE-ACCOUNTS-2-4T).
 *
 * DAB-1431.
 */

/**
 * The billing pages a promo link may point at (`?coupon=CODE` is accepted on each).
 * `/billing` itself resolves to the portal before this sees it.
 */
const COUPON_LANDINGS = new Set(['/billing/portal', '/billing/plans', '/billing/checkout']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const billingStore = useBillingStore();
const userStore = useUserStore();
const { showToast } = useToast();
const { couponTicketTerm, couponValue, discountEnd } = useBillingUtils();

type Step = 'checking' | 'choose';
const step = ref<Step>('checking');
const open = ref(false);
const submitting = ref(false);

// Read once and cleared as the run starts, so a leftover code can't reattach to a
// different account later in the same tab (Switch Account → Add another).
let code = '';
// Whether the code is a Lifetime gift card, once validation has said; null until then.
let isGift: boolean | null = null;

/** The code as validated — held for the choose step so both coupons can be described. */
const incoming = ref<ValidatedCoupon | null>(null);

interface CouponSummary {
  name: string;
  /** The ticket's headline — "50%" or "$5" — or null when the coupon isn't expanded. */
  value: string | null;
  /** How long it lasts: "forever", "for 3 months", "one time". */
  term: string;
  /** Time left on a repeating coupon already ticking; '' otherwise. */
  timeLeft: string;
}

/** Split a coupon into the ticket's headline value and the term printed beside it. */
function ticketParts(coupon: Stripe.Coupon | null): Pick<CouponSummary, 'value' | 'term'> {
  if (!coupon) return { value: null, term: '' };
  return { value: couponValue(coupon), term: couponTicketTerm(coupon) };
}

// Only the subscription's own discounts count here: those are what "Apply new"
// replaces. A customer-level discount (set by support in Stripe) applies to every
// invoice regardless and is left alone, so it doesn't make this a replacement.
const currentDiscountRow = computed(() =>
  pickDiscountFromSources([billingStore.primarySubscription as Record<string, unknown> | null]),
);

const currentCoupon = computed<Stripe.Coupon | null>(() => {
  const row = currentDiscountRow.value;
  if (!row || isUnexpandedDiscountPlaceholder(row)) return null;
  return resolveCouponFromDiscountRow(row);
});

const current = computed<CouponSummary | null>(() => {
  const row = currentDiscountRow.value;
  if (!row) return null;
  const coupon = currentCoupon.value;
  const timeLeft = coupon?.duration === 'repeating' ? discountEnd(row as unknown as Stripe.Discount) : '';
  return {
    name: discountDisplayName(row) || t('billing_coupon'),
    ...ticketParts(coupon),
    timeLeft,
  };
});

const replacement = computed<CouponSummary | null>(() => {
  const validated = incoming.value;
  if (!validated) return null;
  // In the currency the subscription is billed in, as Stripe will charge it.
  const coupon = validated.coupon && couponInCurrency(validated.coupon, billingStore.subscriptionCurrency);
  return {
    name: couponLabel(validated.coupon) || code,
    ...ticketParts(coupon),
    timeLeft: '',
  };
});

/** The two cards of the choose step, in reading order. */
const options = computed(() => [
  { key: 'current', eyebrow: t('billing_coupon_replace_current'), summary: current.value },
  { key: 'new', eyebrow: t('billing_coupon_replace_new'), summary: replacement.value },
]);

type Outcome = 'gift' | 'stashed' | 'applied' | 'replaced' | 'kept' | 'already_applied' | 'lifetime' | 'failed';

// Refusals that say the code can't be spent: it isn't one, or it's switched off, used up or claimed.
const SPENT_CODE_ERRORS = new Set(['COUPON_INVALID', 'COUPON_EXPIRED', 'COUPON_UNREDEEMABLE']);

// Count what the link did, not just that it was clicked — a promo whose codes
// silently fail is exactly the thing nobody notices otherwise (DAB-1303). A gift
// card's code is worth a whole Lifetime plan, so a code goes along only once it
// is known to be no gift, or no longer spendable: a failed lookup (a 503, say)
// could be hiding a live gift.
function record(outcome: Outcome) {
  const error = billingStore.lastApplyErrorCode;
  const sendCode = isGift === false || (isGift === null && SPENT_CODE_ERRORS.has(error ?? ''));
  posthog.capture('billing_coupon_link', {
    outcome,
    error: outcome === 'failed' ? error : null,
    code: sendCode ? code : null,
  });
}

/** Close over the page they landed on; `next` moves them on to another. */
function finish(next?: 'billing-plans' | 'billing-checkout') {
  open.value = false;
  if (next) router.replace({ name: next });
}

function failAndFinish() {
  record('failed');
  showToast('danger', billingStore.lastApplyError ?? t('billing_coupon_error_generic'));
  finish();
}

async function applyAndFinish(validated: ValidatedCoupon, replace: boolean) {
  submitting.value = true;
  try {
    // run() already validated the code to judge it, so apply that result instead of
    // validating it again right before the PUT (RyanHavoc review).
    const ok = await billingStore.applyValidatedCoupon(code, validated, { silent: true, replace });
    if (!ok) return failAndFinish();
    record(replace ? 'replaced' : 'applied');
    showToast('success', t('billing_coupon_applied'));
    finish();
  } finally {
    submitting.value = false;
  }
}

function keepCurrent() {
  record('kept');
  finish();
}

async function run() {
  code = sessionStorage.getItem('coupon') ?? '';
  sessionStorage.removeItem('coupon');
  if (!code) return;

  step.value = 'checking';
  incoming.value = null;
  isGift = null;
  open.value = true;

  await billingStore.fetchBillingOverview();

  // A lifetime plan is a $0 carrier subscription — there is nothing for a
  // discount to take off, so nothing is applied (a fine code would "apply" to no
  // effect and the alert would advertise it forever). The code is only looked up
  // to tell a gift card, which they can pass on, from a coupon.
  if (billingStore.isLifetime) {
    const validated = await billingStore.validateCouponCode(code, { silent: true });
    if (validated) isGift = Boolean(validated.lifetimePrice);
    record('lifetime');
    showToast('info', t(isGift ? 'billing_error_GIFT_LIFETIME_OWNED' : 'billing_coupon_url_lifetime'));
    return finish();
  }

  // Validation runs under `/billing/*`, which needs a Stripe customer; an account
  // that never subscribed may not have one yet (same step as the portal's
  // Subscribe Now). createStripeCustomerOnly toasts its own failure.
  if (!billingStore.hasStripeCustomer) {
    const stripeId = await billingStore.createStripeCustomerOnly({
      uid: userStore.user.uid ?? '',
      email: userStore.user.email ?? '',
      name: userStore.user.name ?? '',
    });
    if (!stripeId) return finish();
  }

  const validated = await billingStore.validateCouponCode(code, { silent: true });
  if (!validated) return failAndFinish();
  isGift = Boolean(validated.lifetimePrice);

  // A gift card is held for its Lifetime checkout whatever plan they're on now
  // (applyValidatedCoupon never puts one on a subscription), and checkout is
  // already on that plan.
  if (isGift) {
    if (!(await billingStore.applyValidatedCoupon(code, validated, { silent: true }))) return failAndFinish();
    record('gift');
    return finish('billing-checkout');
  }

  if (!billingStore.primarySubscription) {
    // Nothing to attach to yet: the validated coupon is stashed for checkout
    // (DAB-848), so they pick a plan with it already in hand.
    if (!(await billingStore.applyValidatedCoupon(code, validated, { silent: true }))) return failAndFinish();
    record('stashed');
    showToast('success', t('billing_coupon_url_checkout_ready'));
    // Plans and checkout already are that step; only the portal moves on.
    return finish(route.name === 'portal' ? 'billing-plans' : undefined);
  }

  if (!currentDiscountRow.value) return applyAndFinish(validated, false);

  // A coupon is already on the subscription: describe both, then let them choose.
  if (validated.coupon && currentCoupon.value && validated.coupon.id === currentCoupon.value.id) {
    record('already_applied');
    showToast('info', t('billing_error_COUPON_ALREADY_APPLIED'));
    return finish();
  }
  // A code their billing can't take between payments is refused, so don't offer it in place of theirs.
  if (billingStore.refusesCoupon(validated.coupon, { silent: true })) return failAndFinish();
  incoming.value = validated;
  step.value = 'choose';
}

// The router's guard stashes the code in sessionStorage (where it survives the
// login bounce), and every landing requires auth, so arriving on one with a code
// stashed is the cue.
// `run` clears the code before its first await, so a navigation it causes (the
// portal → plans hand-off) doesn't start a second run.
watch(
  () => route.path,
  (path) => {
    if (COUPON_LANDINGS.has(path) && sessionStorage.getItem('coupon') && userStore.user.uid) run();
  },
  { immediate: true },
);
</script>

<template>
  <Modal
    v-model="open"
    :title="step === 'choose' ? t('billing_coupon_replace_title') : t('billing_coupon_url_title')"
    :closable="false"
    :size="step === 'choose' ? 'medium' : 'small'"
  >
    <div v-if="step === 'checking'" class="billing-coupon__checking" role="status">
      <svg class="billing-coupon__checking-ticket" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"
        />
        <path d="M9 9h.01M15 15h.01M15 9l-6 6" />
      </svg>
      <Prose align="center" variant="secondary">{{ t('billing_coupon_url_checking') }}</Prose>
    </div>

    <LayoutGap v-else direction="column" size="medium">
      <Prose variant="secondary">{{ t('billing_coupon_replace_body') }}</Prose>

      <div class="billing-coupon__swap-frame">
        <div class="billing-coupon__swap">
          <template v-for="(option, i) in options" :key="option.key">
            <span v-if="i > 0" class="billing-coupon__arrow" aria-hidden="true"><Icon name="arrow-right" /></span>
            <div class="billing-coupon__option" :class="`billing-coupon__option--${option.key}`">
              <div class="billing-coupon__ticket">
                <div class="billing-coupon__stub">
                  <template v-if="option.summary?.value">
                    <span
                      class="billing-coupon__value"
                      :class="{ 'billing-coupon__value--long': option.summary.value.length > 5 }"
                      >{{ option.summary.value }}</span
                    >
                    <span class="billing-coupon__off">{{ t('billing_coupon_ticket_off') }}</span>
                  </template>
                  <svg v-else class="billing-coupon__glyph" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"
                    />
                    <path d="M9 9h.01M15 15h.01M15 9l-6 6" />
                  </svg>
                </div>
                <div class="billing-coupon__details">
                  <span class="billing-coupon__eyebrow">{{ option.eyebrow }}</span>
                  <strong class="billing-coupon__name">{{ option.summary?.name }}</strong>
                  <span v-if="option.summary?.term" class="billing-coupon__term">{{ option.summary.term }}</span>
                  <span v-if="option.summary?.timeLeft" class="billing-coupon__time-left">
                    {{ t('billing_coupon_ticket_time_left', { time: option.summary.timeLeft }) }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <LayoutGap direction="row" size="small" align="right">
        <Button variant="secondary" finish="outlined" :disabled="submitting" @click="keepCurrent">
          {{ t('billing_coupon_replace_keep') }}
        </Button>
        <Button
          variant="primary"
          :disabled="submitting || !incoming"
          @click="incoming && applyAndFinish(incoming, true)"
        >
          {{ t('billing_coupon_replace_apply') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </Modal>
</template>

<style lang="scss" scoped>
// Each coupon is drawn as a ticket: a stub carrying the headline discount, a
// perforation, and the details. The current one sits back in neutral; the new
// one is gold, the same warm wash as the lifetime plan card on the portal.
.billing-coupon {
  &__checking {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('small');
    padding: space('small') 0;
  }

  &__checking-ticket {
    width: 3.5rem;
    height: 3.5rem;
    fill: theme('color-brand-gold-tint');
    stroke: theme('color-brand-gold');
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: billing-coupon-wiggle 1.6s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  // Two tickets with an arrow between; stacked, with the arrow pointing down,
  // when the modal is too narrow to hold them side by side. The frame is the
  // query container (a container can't restyle itself by its own width).
  &__swap-frame {
    container-type: inline-size;
    align-self: stretch;
  }

  &__swap {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: stretch;
    gap: space('small');
  }

  &__arrow {
    display: grid;
    place-items: center;
    align-self: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: theme('color-fg-app');
    box-shadow: 0 1px 3px rgba(27, 25, 24, 0.12);
    color: theme('color-brand-gold-text');
  }

  @container (max-width: 30rem) {
    &__swap {
      grid-template-columns: minmax(0, 1fr);
    }

    &__arrow {
      justify-self: center;
      transform: rotate(90deg);
    }
  }

  // The drop shadow lives on this wrapper so it follows the ticket's notched
  // outline (a box-shadow on the masked ticket would be cut away with it).
  &__option {
    --ticket-stub: 6.25rem;
    --ticket-notch: 0.55rem;
    --ticket-bg: #{theme('color-bg-app')};
    --ticket-stub-bg: #{theme('color-bg-interactive-hover-solid')};
    --ticket-ink: #{theme('color-text-secondary')};
    --ticket-rule: #{theme('color-border-primary')};

    display: flex;
    filter: drop-shadow(0 1px 2px rgba(27, 25, 24, 0.12));
  }

  &__option--new {
    --ticket-bg: #fffaf0;
    --ticket-stub-bg: #{theme('color-brand-gold-tint')};
    --ticket-ink: #{theme('color-brand-gold-text')};
    --ticket-rule: #e6c97c;

    filter: drop-shadow(0 6px 14px rgba(180, 122, 2, 0.2)) drop-shadow(0 1px 2px rgba(140, 95, 2, 0.15));
  }

  &__ticket {
    flex: 1;
    display: flex;
    min-height: 7rem;
    border-radius: borderRadius('medium');
    background: var(--ticket-bg);
    // A half-circle bite out of the top and bottom edge where stub meets body.
    mask:
      radial-gradient(circle var(--ticket-notch) at var(--ticket-stub) 0, transparent 98%, #000) top / 100% 51%
        no-repeat,
      radial-gradient(circle var(--ticket-notch) at var(--ticket-stub) 100%, transparent 98%, #000) bottom / 100% 51%
        no-repeat;
  }

  &__option--new &__ticket {
    background:
      radial-gradient(120% 160% at 100% 0%, rgba(245, 165, 3, 0.16) 0%, transparent 60%),
      radial-gradient(120% 160% at 100% 110%, rgba(74, 217, 217, 0.1) 0%, transparent 55%), var(--ticket-bg);
  }

  &__stub {
    flex: 0 0 var(--ticket-stub);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: space('small') space('xsmall');
    background: var(--ticket-stub-bg);
    color: var(--ticket-ink);
    text-align: center;

    // The perforation, kept clear of the notches.
    &::after {
      content: '';
      position: absolute;
      inset: calc(var(--ticket-notch) + #{space('tiny')}) 0;
      left: auto;
      border-right: 2px dashed var(--ticket-rule);
    }
  }

  &__value {
    font-family: fontFamily('secondary');
    font-size: fontSize('xlarge');
    font-weight: fontWeight('bold');
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }

  &__value--long {
    font-size: fontSize('large');
  }

  &__off {
    margin-top: space('xxsmall');
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__glyph {
    width: 2.25rem;
    height: 2.25rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &__details {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: space('xxsmall');
    padding: space('small') space('medium');
  }

  &__eyebrow {
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: theme('color-text-placeholder');
  }

  &__option--new &__eyebrow {
    color: theme('color-brand-gold-text');
  }

  &__name {
    font-size: fontSize('medium');
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  &__term {
    font-size: fontSize('small');
    color: theme('color-text-secondary');
  }

  &__time-left {
    align-self: flex-start;
    margin-top: space('xxsmall');
    padding: 0.1rem space('xsmall');
    border-radius: 999px;
    background: theme('color-bg-interactive-pressed');
    font-size: fontSize('xsmall');
    color: theme('color-text-secondary');
  }

  &__option--current &__name {
    color: theme('color-text-secondary');
  }
}

@keyframes billing-coupon-wiggle {
  0%,
  100% {
    transform: rotate(-6deg) translateY(0);
  }

  50% {
    transform: rotate(6deg) translateY(-3px);
  }
}
</style>
